import { useState, useEffect } from "react";

/**
 * Track the visual viewport height so UI can avoid the mobile virtual
 * keyboard. Returns the current `window.visualViewport.height` in pixels,
 * or `undefined` when disabled or when the API is unavailable.
 *
 * Usage: set the returned value as a CSS custom property (e.g. `--visual-vh`)
 * and reference it in a class like `h-(--visual-vh,100dvh)` so the element
 * shrinks to the visible area when the keyboard opens.
 */
export function useVisualViewportHeight(enabled: boolean): number | undefined {
  const [height, setHeight] = useState<number | undefined>();

  useEffect(() => {
    if (!enabled || !window.visualViewport) {
      setHeight(undefined);
      return;
    }

    const vv = window.visualViewport;
    const update = () => setHeight(vv.height);

    update();
    vv.addEventListener("resize", update);
    return () => vv.removeEventListener("resize", update);
  }, [enabled]);

  return height;
}
